import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Building,
  Clock,
  Users,
  ArrowRight,
  Star,
  Calendar,
  MessageSquare
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const PropertyManagementCleaningPage = () => {
  const services = [
    {
      title: "Turnover Cleaning",
      description: "Move-out to move-in cleaning for residential rental units. Fast, thorough, and inspection-ready for the next tenant."
    },
    {
      title: "Vacant Unit Preparation",
      description: "Deep cleaning for units between tenants. Get properties show-ready and lease-ready without delay."
    },
    {
      title: "Common Area Maintenance",
      description: "Lobbies, hallways, stairwells, laundry rooms, and shared spaces. Recurring cleaning to keep properties looking maintained."
    },
    {
      title: "Multi-Property Coordination",
      description: "Managing multiple properties? We coordinate cleaning across your portfolio and keep you updated on schedules and any concerns."
    }
  ];

  const whatWeClean = [
    "Full turnover cleaning (kitchen, bathrooms, all rooms)",
    "Floors (vacuum, mop, spot clean)",
    "Appliances (oven, refrigerator, microwave)",
    "Cabinets and drawers (inside and out)",
    "Windows, blinds, and windowsills",
    "Baseboards, doors, and light switches",
    "Common area lobbies and hallways",
    "Trash removal and final walkthrough"
  ];

  const whyChoose = [
    "Fast turnaround times for urgent move-ins",
    "Same team for consistency across your properties",
    "Direct communication—call or text Bob and Heather",
    "Flexible scheduling to match your leasing calendar",
    "15+ years of professional cleaning experience",
    "Insured and bonded for your protection"
  ];

  const faqs = [
    {
      question: "How quickly can you turn around a unit after move-out?",
      answer: "Most turnovers are completed within 24-48 hours, depending on the unit size and condition. For urgent move-ins, call us at 321-408-1347—we'll work with you to meet your timeline when possible."
    },
    {
      question: "Do you work with property managers who have multiple units?",
      answer: "Yes. Many of our property management clients have multiple units or properties. We coordinate cleaning across your portfolio and keep you updated on schedules, completed work, and any issues we notice during cleaning."
    },
    {
      question: "Can you help with common area cleaning?",
      answer: "Absolutely. We provide recurring common area cleaning for lobbies, hallways, stairwells, laundry rooms, and shared spaces. It's an add-on service many of our property management clients use to keep properties looking maintained."
    },
    {
      question: "What if a unit is in rough condition after move-out?",
      answer: "We'll assess the situation and let you know what's needed. For heavily soiled units, we provide deep cleaning or post-construction-level cleaning. We're honest about timelines and costs, so you can plan accordingly."
    },
    {
      question: "How do you handle keys and building access?",
      answer: "We work with your system—lockboxes, property management software, onsite coordinators, or direct key exchanges. We're experienced and flexible with access coordination."
    },
    {
      question: "Do you provide reports or updates after cleaning?",
      answer: "Yes. After each turnover, we can provide a brief update on the unit's condition and any concerns we noticed (damage, maintenance issues, etc.). Many property managers find this helpful for planning."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "Property Management Cleaning Services in Brevard County | Pierce Premier",
      description: "Turnover cleaning, vacant unit prep, and common area maintenance for Brevard County property managers. Fast, reliable, insured. Call 321-408-1347.",
      url: "/services/commercial/property-management"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Services", url: "/services" },
      { name: "Commercial Cleaning", url: "/services/commercial" },
      { name: "Property Management" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "Property Management Cleaning Services",
      description: "Professional turnover cleaning, vacant unit preparation, and common area maintenance for Brevard County property managers and landlords."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="Property Management Cleaning Services in Brevard County | Pierce Premier"
        description="Turnover cleaning, vacant unit prep, and common area maintenance for Brevard County property managers. Fast, reliable, insured. Call 321-408-1347."
        canonical="/services/commercial/property-management"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Building className="w-5 h-5 text-teal-600" />
                  <Badge className="bg-teal-600 text-white">Multi-Unit Specialists</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  Property Management Cleaning Services in Brevard County
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Property managers need cleaning they can count on—fast turnovers, consistent quality, and reliable communication. Pierce Premier handles turnover cleaning, vacant unit preparation, and common area maintenance for Brevard County property managers who need dependable service and direct access to the people doing the work.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  We're Bob and Heather—a husband-and-wife team serving property managers throughout Brevard County. Whether you're managing individual units or coordinating cleaning across multiple properties, you'll work directly with us and have the same cleaning team handling your turnovers. We've worked with single-property landlords and multi-unit managers across the area.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
                  >
                    Discuss Your Properties
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-teal-600 text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-teal-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=800&q=80" 
                  alt="Property management cleaning and turnover services in Brevard County, Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Property Managers Choose Pierce Premier
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Turnaround Times</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We understand property managers work on tight timelines. Move-out to move-in cleaning typically happens within 24-48 hours, and we're flexible with urgent requests when you need a unit ready fast.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Direct Communication</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No phone trees or ticket systems. You'll have direct access to Bob and Heather. Call, text, or email—we respond quickly and keep you updated on schedules and any issues we notice.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Same Team, Every Property</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Whether it's one unit or multiple properties, you'll have the same cleaners handling your turnovers. That consistency means better quality control and fewer surprises.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">15+ Years of Experience</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Heather brings over 15 years of executive housekeeping experience. We know what inspection-ready looks like, and we deliver quality that helps you lease units faster.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  How Property Management Cleaning Works
                </h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Initial Consultation</h3>
                      <p className="text-gray-600">We discuss your properties, typical turnover needs, access procedures, and scheduling. We'll answer your questions and set up a system that works for you.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Ongoing Coordination</h3>
                      <p className="text-gray-600">As units turn over, you contact us with the address, keys/access, and move-in date. We schedule cleaning and confirm completion with you.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Updates & Reporting</h3>
                      <p className="text-gray-600">After cleaning, we update you on completion and any maintenance issues we noticed. You'll always know the status of your turnovers.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Services for Property Managers
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-teal-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What We Clean During Turnovers
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {whatWeClean.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-teal-100">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Property Managers Work With Us Long-Term
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {whyChoose.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-teal-50 rounded-lg border border-teal-100">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Service Areas
              </h2>
              <p className="text-gray-600">Serving property managers throughout Brevard County</p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Palm Bay", link: "/areas/palm-bay" },
                { name: "Melbourne", link: "/areas/melbourne" },
                { name: "Viera", link: "/areas/viera" },
                { name: "Rockledge", link: "/areas/rockledge" },
                { name: "Cocoa", link: "/areas/cocoa" },
                { name: "West Melbourne", link: "/areas/west-melbourne" }
              ].map((city, idx) => (
                <Link key={idx} to={city.link} className="p-4 bg-teal-50 rounded-lg border-2 border-teal-200 hover:border-teal-400 transition-colors text-center group">
                  <span className="text-teal-700 font-semibold group-hover:text-teal-900">{city.name}</span>
                  <ArrowRight className="w-4 h-4 inline-block ml-1 text-teal-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Property Managers
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Related Commercial Services
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/services/commercial/real-estate-cleaning" className="p-6 bg-white rounded-lg border-2 border-gray-200 hover:border-teal-400 transition-colors group">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600">Real Estate & Listing Prep</h3>
                <p className="text-gray-600 text-sm">Pre-listing cleaning and open house preparation for Realtors</p>
              </Link>
              <Link to="/services/move-in-move-out-cleaning" className="p-6 bg-white rounded-lg border-2 border-gray-200 hover:border-teal-400 transition-colors group">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600">Move-In / Move-Out Cleaning</h3>
                <p className="text-gray-600 text-sm">Residential move-in and move-out cleaning services</p>
              </Link>
            </div>
          </div>
        </section>

        <CTASection 
          title="Let's Discuss Your Turnover Cleaning Needs"
          description="Whether you manage one property or multiple units, let's talk about reliable cleaning that matches your leasing schedule."
          primaryButtonText="Talk About Your Properties"
        />
      </div>
    </>
  );
};

export default PropertyManagementCleaningPage;
